/**
 * Session counts as the agent reports them. A long transcript is first read from its
 * tail, so its turns, tool calls and tokens are lower bounds (`partial`) until the agent
 * has read it whole; and the subagent and workflow lists are bounded, with the totals
 * alongside.
 */
import type { Session } from '@orgus/protocol';
import { formatCompact, formatInt } from '@orgus/ui';

export const PARTIAL_HINT = 'At least this many: counted from the most recent part of a long transcript. The full count appears once the agent has read all of it.';

/** A count, with a trailing '+' when it is a lower bound. */
export function lowerBound(n: number, partial: boolean | undefined, format: (n: number) => string = formatInt): string {
  return partial ? `${format(n)}+` : format(n);
}

export const compactLowerBound = (n: number, partial: boolean | undefined): string => lowerBound(n, partial, formatCompact);

/** How many subagents the session has, not just how many the list holds. */
export function subagentTotal(s: Pick<Session, 'subagents' | 'subagentsTotal'>): number {
  return Math.max(s.subagentsTotal ?? 0, s.subagents.length);
}

/** How many workflow runs the session has, not just how many the list holds. */
export function workflowTotal(s: Pick<Session, 'workflows' | 'workflowsTotal'>): number {
  return Math.max(s.workflowsTotal ?? 0, s.workflows.length);
}


/**
 * How many lanes the drawer's Agents tab lists: one per subagent and workflow the session
 * holds. The tab's count is this number, so it always matches the rows under it; the totals
 * (which can be larger, the lists are bounded) go in the tab's caption instead.
 */
export function agentRowCount(s: Pick<Session, 'subagents' | 'workflows'>): number {
  return s.subagents.length + s.workflows.length;
}

/** "The 4 most recent of 13 are listed.", or null when the lists hold everything. */
export function agentsShownNote(s: Pick<Session, 'subagents' | 'subagentsTotal' | 'workflows' | 'workflowsTotal'>): string | null {
  const rows = agentRowCount(s);
  const total = subagentTotal(s) + workflowTotal(s);
  if (rows >= total) return null;
  return `The ${formatInt(rows)} most recent of ${formatInt(total)} are listed.`;
}
